<template>
    <div class="panel panel-default">
        <div class="panel-heading">
            <div class="row">
                <div class="col-xs-21">
                    <h5>
                        Total Breakdown
                    </h5>
                </div>

                <div class="col-xs-3" style="padding-top: 3px">
                    <select-widget
                        v-model="timeUnit"
                        :options="[{value: 'years', label: 'Years'}, {value: 'months', label: 'Months'}, {value: 'weeks', label: 'Weeks'}]"
                        class="input-sm"
                    ></select-widget>
                </div>
            </div>
        </div>

        <loading-spinner v-if="loading" :style="{margin: '30px'}"></loading-spinner>

        <div v-else class="panel-body">
            <div v-if="isEmpty" class="text-center" style="padding: 50px">
                <i>
                    There are no sales to display. Try adjusting the filters.
                </i>
            </div>

            <canvas v-else width="800" height="250"></canvas>
        </div>
    </div>
</template>

<script>
import datalabels from 'chartjs-plugin-datalabels'
import http from '@/http'
import moment from 'moment'
import LoadingSpinner from '@/components/Layout/LoadingSpinner'
import SelectWidget from '@/vendor/forms/FormGroups/Widgets/Select'

const totalizer = {
    id: 'totalizer',
    beforeUpdate: function (chart) {
        var totals = []

        var utmost = {}

        chart.data.datasets.forEach(function (dataset, datasetIndex) {
            if (chart.isDatasetVisible(datasetIndex)) {
                var stack = dataset.stack

                utmost[stack] = datasetIndex

                dataset.data.forEach(function (value, index) {
                    if (totals[index] === undefined) {
                        totals[index] = {}
                    }

                    totals[index][stack] = (totals[index][stack] || 0) + value
                })
            }
        })

        chart.$totalizer = {
            totals: totals,
            utmost: utmost
        }
    }
}

export default {
    components: {LoadingSpinner, SelectWidget},

    props: {
        compare: {required: true},

        adFilters: {required: true},
        subscriptionFilters: {required: true},
        compareAdFilters: {required: true},
        compareSubscriptionFilters: {required: true}
    },

    data() {
        return {
            baseAdRows: null,
            baseSubscriptionRows: null,
            compareAdRows: null,
            compareSubscriptionRows: null,

            chart: null,

            timeUnit: 'months',

            loading: true
        }
    },

    watch: {
        adFilters() {
            this.load()
        },

        subscriptionFilters() {
            this.load()
        },

        compare() {
            this.load()
        },

        compareAdFilters() {
            this.load()
        },

        compareSubscriptionFilters() {
            this.load()
        },

        timeUnit() {
            this.load()
        },

        loading(value) {
            if (!value && !this.isEmpty) {
                this.$nextTick(this.draw)
            }
        }
    },

    mounted() {
        this.load()
    },

    computed: {
        isEmpty() {
            if (this.compare) {
                return this.baseAdRows.length === 0 && this.compareAdRows.length === 0 && this.baseSubscriptionRows.length === 0 && this.compareSubscriptionRows.length === 0
            }

            return this.baseAdRows.length === 0 && this.baseSubscriptionRows.length === 0
        },

        baseSoldFrom() {
            return this.adFilters.find(filter => filter.dimension === 'sales_date' && filter.operator === '>=').value
        },

        baseSoldTo() {
            return this.adFilters.find(filter => filter.dimension === 'sales_date' && filter.operator === '<=').value
        },

        baseTimeItems() {
            return this.baseTimes.map(baseTime => baseTime.format(this.timeFormat))
        },

        baseTimes() {
            let actual = moment(this.baseSoldFrom).startOf(this.startOfTimeUnit)

            let timeUnit = []

            while (actual <= moment(this.baseSoldTo)) {
                timeUnit.push(moment(actual))

                actual.add(1, this.timeUnit)
            }

            return timeUnit
        },

        compareSoldFrom() {
            return this.compareAdFilters.find(filter => filter.dimension === 'sales_date' && filter.operator === '>=').value
        },

        compareSoldTo() {
            return this.compareAdFilters.find(filter => filter.dimension === 'sales_date' && filter.operator === '<=').value
        },

        compareTimeItems() {
            return this.compareTimes.map(compareTime => compareTime.format(this.timeFormat))
        },

        compareTimes() {
            let actual = moment(this.compareSoldFrom).startOf(this.startOfTimeUnit)

            let timeUnit = []

            while (actual <= moment(this.compareSoldTo)) {
                timeUnit.push(moment(actual))

                actual.add(1, this.timeUnit)
            }

            return timeUnit
        },

        datasets() {
            return this.baseDatasets.concat(this.compareDatasets)
        },

        baseDatasets() {
            return [
                {
                    label: 'Ads',
                    data: this.baseTimeItems.map(timeItem => {
                        let row = this.baseAdRows.find(row => row[this.timeUnitKey] === timeItem)

                        return row ? (row['publication_ad_revenue'] || 0) + (row['time_ad_revenue'] || 0) : 0
                    }),
                    backgroundColor: '#62d26f',
                    stack: 1
                },
                {
                    label: 'Subscriptions',
                    data: this.baseTimeItems.map(timeItem => {
                        let row = this.baseSubscriptionRows.find(row => row[this.timeUnitKey] === timeItem)

                        return row ? (row['subscription_revenue'] || 0) : 0
                    }),
                    backgroundColor: '#20aaea',
                    stack: 1
                }
            ]
        },

        compareDatasets() {
            if (!this.compare) {
                return []
            }

            return [
                {
                    label: 'Ads',
                    data: this.compareTimeItems.map(timeItem => {
                        let row = this.compareAdRows.find(row => row[this.timeUnitKey] === timeItem)

                      return row ? (row['publication_ad_revenue'] || 0) + (row['time_ad_revenue'] || 0) : 0
                    }),
                    backgroundColor: '#62d26f',
                    stack: 2
                },
                {
                    label: 'Subscriptions',
                    data: this.compareTimeItems.map(timeItem => {
                        let row = this.compareSubscriptionRows.find(row => row[this.timeUnitKey] === timeItem)

                      return row ? (row['subscription_revenue'] || 0) : 0
                    }),
                    backgroundColor: '#20aaea',
                    stack: 2
                }
            ]

        },

        labels() {
            let labels = []

            for (let i = 0; i < this.baseTimeItems.length; i++) {
                let label = this.baseTimeItems[i]

                if (this.compare && typeof this.compareTimeItems[i] !== 'undefined') {
                    label = label + ' (' + this.compareTimeItems[i] + ')'
                }

                labels.push(label)
            }

            return labels
        },

        timeFormat() {
            if (this.timeUnit === 'years') {
                return 'YYYY'
            }

            if (this.timeUnit === 'months') {
                return 'YYYY-MM'
            }

            return 'GGGG-WW'
        },

        timeUnitKey() {
            if (this.timeUnit === 'years') {
                return 'sales_year'
            }

            if (this.timeUnit === 'months') {
                return 'sales_month'
            }

            return 'sales_week'
        },

        startOfTimeUnit() {
            if (this.timeUnit === 'years') {
                return 'year'
            }

            if (this.timeUnit === 'months') {
                return 'month'
            }

            return 'isoWeek'
        }
    },

    methods: {
        load() {
            this.loading = true

            Promise.all(this.getPromises())
                .then(responses => {
                    this.baseAdRows = responses[0].data.data

                    this.baseSubscriptionRows = responses[1].data.data

                    if (this.compare) {
                        this.compareAdRows = responses[2].data.data

                        this.compareSubscriptionRows = responses[3].data.data
                    }

                    this.loading = false
                })
        },

        getPromises() {
            let promises = [
                http.post('/api/v1/reports/leadout-dashboard', {
                    metrics: ['publication_ad_revenue', 'time_ad_revenue'],
                    filters: this.adFilters,
                    dimensions: [this.timeUnitKey]
                }),
                http.post('/api/v1/reports/leadout-dashboard', {
                    metrics: ['subscription_revenue'],
                    filters: this.subscriptionFilters,
                    dimensions: [this.timeUnitKey]
                })
            ]

            if (this.compare) {
                promises.push(
                    http.post('/api/v1/reports/leadout-dashboard', {
                        metrics: ['publication_ad_revenue', 'time_ad_revenue'],
                        filters: this.compareAdFilters,
                        dimensions: [this.timeUnitKey]
                    })
                )

                promises.push(
                    http.post('/api/v1/reports/leadout-dashboard', {
                        metrics: ['subscription_revenue'],
                        filters: this.compareSubscriptionFilters,
                        dimensions: [this.timeUnitKey]
                    })
                )
            }

            return promises
        },

        draw() {
            this.chart = new Chart(this.$el.querySelector('canvas').getContext('2d'), {
                plugins: [datalabels, totalizer],

                type: 'bar',

                data: {
                    labels: this.labels,
                    datasets: this.datasets
                },

                options: {
                    scales: {
                        xAxes: [{
                            stacked: true
                        }],

                        yAxes: [{
                            stacked: true,
                            ticks: {
                                beginAtZero: true,
                                callback: value => this.$options.filters.price(value)
                            }
                        }]
                    },

                    legend: {
                        position: 'bottom',
                        onClick: () => {
                        },
                        labels: {
                            generateLabels: () => {
                                return [
                                    {
                                        text: 'Ads',
                                        fillStyle: '#62d26f'
                                    },
                                    {
                                        text: 'Subscriptions',
                                        fillStyle: '#20aaea'
                                    }
                                ]
                            }
                        }
                    },

                    layout: {
                        padding: {
                            top: 25
                        }
                    },

                    plugins: {
                        datalabels: {
                            anchor: 'end',
                            align: 'end',
                            formatter: (value, ctx) => this.$options.filters.price(ctx.chart.$totalizer.totals[ctx.dataIndex][ctx.dataset.stack]),
                            display: ctx => ctx.datasetIndex === ctx.chart.$totalizer.utmost[ctx.dataset.stack]
                        }
                    },

                    tooltips: {
                        callbacks: {
                            label: (t, d) => d.datasets[t.datasetIndex].label + ': ' + this.$options.filters.price(d.datasets[t.datasetIndex].data[t.index]),
                            title: (t, d) => {
                                if (d.datasets[t[0].datasetIndex].stack === 1) {
                                    return this.baseTimeItems[t[0].index]
                                }

                                return this.compareTimeItems[t[0].index]
                            }
                        }
                    }
                }
            })
        }
    }
}
</script>
